import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Household, HouseholdMember, HouseholdInvite } from '@/types/household'
import { householdApi } from '@/services/household-api'

interface HouseholdState {
  currentHousehold: Household | null
  households: Household[]
  members: HouseholdMember[]
  invites: HouseholdInvite[]
  loading: boolean
  error: string | null
  viewMode: 'personal' | 'household'
}

interface HouseholdActions {
  loadHouseholds: () => Promise<void>
  createHousehold: (name: string) => Promise<Household | null>
  updateHousehold: (id: string, name: string) => Promise<Household | null>
  deleteHousehold: (id: string) => Promise<boolean>
  setCurrentHousehold: (household: Household | null) => void
  loadMembers: (householdId: string) => Promise<void>
  removeMember: (householdId: string, memberId: string) => Promise<boolean>
  updateMemberRole: (householdId: string, memberId: string, role: 'admin' | 'member') => Promise<boolean>
  leaveHousehold: (householdId: string) => Promise<boolean>
  loadInvites: (householdId: string) => Promise<void>
  createInvite: (householdId: string, email: string, role: 'admin' | 'member') => Promise<HouseholdInvite | null>
  deleteInvite: (inviteId: string) => Promise<boolean>
  acceptInvite: (inviteCode: string) => Promise<boolean>
  setViewMode: (mode: 'personal' | 'household') => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

type HouseholdStore = HouseholdState & HouseholdActions

export const useHouseholdStore = create<HouseholdStore>()(
  persist(
    (set, get) => ({
      currentHousehold: null,
      households: [],
      members: [],
      invites: [],
      loading: false,
      error: null,
      viewMode: 'personal',

      loadHouseholds: async () => {
        set({ loading: true, error: null })
        try {
          const households = await householdApi.getHouseholds()
          const persistedHousehold = get().currentHousehold
          const resolvedCurrentHousehold = persistedHousehold
            ? households.find((household) => household.id === persistedHousehold.id) ?? null
            : null

          set({
            households,
            currentHousehold: resolvedCurrentHousehold,
            members: resolvedCurrentHousehold ? get().members : [],
            invites: resolvedCurrentHousehold ? get().invites : [],
            viewMode:
              get().viewMode === 'household' && resolvedCurrentHousehold
                ? 'household'
                : 'personal',
            loading: false,
          })
        } catch (error) {
          set({
            households: [],
            currentHousehold: null,
            members: [],
            invites: [],
            viewMode: 'personal',
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load households',
          })
        }
      },

      createHousehold: async (name) => {
        set({ loading: true, error: null })
        try {
          const household = await householdApi.createHousehold(name)
          set((state) => ({
            households: [household, ...state.households],
            currentHousehold: household,
            viewMode: 'household',
            loading: false,
          }))
          return household
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to create household',
          })
          return null
        }
      },

      updateHousehold: async (id, name) => {
        set({ loading: true, error: null })
        try {
          const household = await householdApi.updateHousehold(id, { name })
          set((state) => ({
            households: state.households.map((item) => (item.id === id ? household : item)),
            currentHousehold: state.currentHousehold?.id === id ? household : state.currentHousehold,
            loading: false,
          }))
          return household
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to update household',
          })
          return null
        }
      },

      deleteHousehold: async (id) => {
        set({ loading: true, error: null })
        try {
          await householdApi.deleteHousehold(id)
          set((state) => ({
            households: state.households.filter((item) => item.id !== id),
            currentHousehold: state.currentHousehold?.id === id ? null : state.currentHousehold,
            viewMode: state.currentHousehold?.id === id ? 'personal' : state.viewMode,
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to delete household',
          })
          return false
        }
      },

      setCurrentHousehold: (household) =>
        set({
          currentHousehold: household,
          viewMode: household ? 'household' : 'personal',
        }),

      loadMembers: async (householdId) => {
        set({ loading: true, error: null })
        try {
          const members = await householdApi.getHouseholdMembers(householdId)
          set({ members, loading: false })
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load members',
          })
        }
      },

      removeMember: async (householdId, memberId) => {
        set({ loading: true, error: null })
        try {
          await householdApi.removeHouseholdMember(householdId, memberId)
          set((state) => ({
            members: state.members.filter((member) => member.id !== memberId),
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to remove member',
          })
          return false
        }
      },

      updateMemberRole: async (householdId, memberId, role) => {
        set({ loading: true, error: null })
        try {
          const member = await householdApi.updateHouseholdMemberRole(householdId, memberId, role)
          set((state) => ({
            members: state.members.map((item) => (item.id === memberId ? member : item)),
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to update member role',
          })
          return false
        }
      },

      leaveHousehold: async (householdId) => {
        set({ loading: true, error: null })
        try {
          await householdApi.leaveHousehold(householdId)
          set((state) => ({
            households: state.households.filter((item) => item.id !== householdId),
            currentHousehold: state.currentHousehold?.id === householdId ? null : state.currentHousehold,
            viewMode: state.currentHousehold?.id === householdId ? 'personal' : state.viewMode,
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to leave household',
          })
          return false
        }
      },

      loadInvites: async (householdId) => {
        set({ loading: true, error: null })
        try {
          const invites = await householdApi.getHouseholdInvites(householdId)
          set({ invites, loading: false })
        } catch (error) {
          set({
            invites: [],
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load invites',
          })
        }
      },

      createInvite: async (householdId, email, role) => {
        set({ loading: true, error: null })
        try {
          const invite = await householdApi.createHouseholdInvite(householdId, email, role)
          set((state) => ({
            invites: [invite, ...state.invites],
            loading: false,
          }))
          return invite
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to create invite',
          })
          return null
        }
      },

      deleteInvite: async (inviteId) => {
        set({ loading: true, error: null })
        try {
          await householdApi.deleteHouseholdInvite(inviteId)
          set((state) => ({
            invites: state.invites.filter((invite) => invite.id !== inviteId),
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to delete invite',
          })
          return false
        }
      },

      acceptInvite: async (inviteCode) => {
        set({ loading: true, error: null })
        try {
          const household = await householdApi.acceptHouseholdInvite(inviteCode)
          set((state) => ({
            households: [household, ...state.households.filter((item) => item.id !== household.id)],
            currentHousehold: household,
            viewMode: 'household',
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to accept invite',
          })
          return false
        }
      },

      setViewMode: (mode) => set({ viewMode: mode }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'household-storage',
      partialize: (state) => ({
        currentHousehold: state.currentHousehold,
        households: state.households,
        viewMode: state.viewMode,
      }),
    }
  )
)
